'use client';
import Link from 'next/link';
import Image from 'next/image';
import moment from 'moment';

import { buttonVariants } from '../Button';

import { TranslatedBlogArticle } from '@/types/Blog';
import { cn } from '@/lib/utils';
import { AnimatedLink } from '../AnimatedLink';
import { useRouter } from 'next/navigation';
import getMediaFilterUrl from '@/utils/get-media-filter';
import CreateMediaFilterTrigger from '@/components/Media/CreateMediaFilterTrigger';

type Props = {
  article: TranslatedBlogArticle;
  lang: string;
  readMoreLabel: string;
  atCarousel?: boolean;
};

export default function BlogArticleListItem({
  article,
  lang,
  readMoreLabel,
  atCarousel,
}: Props) {
  const { push } = useRouter();
  const articleDate = moment(article.publicationDate).utc();

  function getArticleLink() {
    if (lang == 'en') {
      return `/blog/${article.blogArticleTranslation.slug}`;
    }
    return `/${lang}/blog/${article.blogArticleTranslation.slug}`;
  }

  const filterName = 'large_1-1_webp';

  const filteredArticleImage = getMediaFilterUrl({
    mediaFilters: article?.image?.mediaFilters,
    filterName,
  });

  const reportNoImg = () => {
    console.log('[ARTICLE LIST] No image path on image');
    return (
      <Image
        src={`${process.env.NEXT_PUBLIC_S3_URI}/assets/images/placeholder.jpg`}
        alt={article.blogArticleTranslation.title}
        width={100}
        height={100}
        quality={85}
        className="w-full relative inline-block align-middle h-auto max-w-full"
        sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
      />
    );
  };

  return (
    <div
      className={cn(
        'flex justify-center w-full [@media(min-width:1100px)]:w-[calc(100%-20px)]',
        {
          'h-auto my-[.625rem] mx-[.625rem] md:my-[.9375rem] md:mx-[.9375rem] w-[calc(100%-1.25rem)] md:w-[calc(50%-1.875rem)] [@media(min-width:1100px)]:w-[calc(50%-1.875rem)] [@media(min-width:1690px)]:w-[calc(33.33333%-1.875rem)] flex-[0_0_auto]':
            !atCarousel,
        },
      )}>
      <CreateMediaFilterTrigger
        imageUrl={filteredArticleImage}
        mediaId={article?.image?.id}
        filterName={filterName}
      />
      <div className="flex flex-col group gap-4 bg-white shadow-default outline-none overflow-hidden relative w-4/5 desktop:w-full desktop:transition-all desktop:hover:shadow-[0_20px_40px_0_rgba(0,0,0,.3)] desktop:hover:translate-y-[-3%] desktop:duration-300 desktop:ease-in-out">
        <div>
          <Link href={getArticleLink()}>
            {article.image && article.image.path ? (
              <Image
                src={
                  filteredArticleImage ??
                  `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/${filterName}/${article.image.path}${article.image.filename}`
                }
                alt={article.blogArticleTranslation.title}
                width={100}
                height={100}
                quality={85}
                className="w-full relative inline-block align-middle h-auto max-w-full"
                sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
              />
            ) : (
              reportNoImg()
            )}
          </Link>
        </div>
        <div className="relative flex flex-col self-stretch min-h-[280px] desktop:h-[280px] px-[1.125rem] pb-[1.125rem] text-left">
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden desktop:gap-4 self-stretch text-left">
            <div className="text-base flex flex-col md:flex-row items-start md:items-center gap-[0.3rem] md:gap-2 z-50">
              <div>{articleDate.format('DD/MM/YYYY')}</div>
              <div className="hidden md:block w-6 h-[.0625rem] bg-gray-700" />
              <div className="flex items-center gap-[0.3rem]">
                {article.categories.map(({ blogCategoryTranslation }) => (
                  <Link
                    key={blogCategoryTranslation.title}
                    className=""
                    href={
                      lang == 'en'
                        ? `/blog/categories/${blogCategoryTranslation.slug}`
                        : `/${lang}/blog/categories/${blogCategoryTranslation.slug}`
                    }>
                    <span className="cursor-pointer text-blue font-semibold">
                      {blogCategoryTranslation.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <Link
              href={getArticleLink()}
              className="flex min-h-0 flex-1 flex-col self-stretch overflow-hidden">
              <div className="flex items-start">
                <h3 className="flex-1 text-xl mb-2 lg:text-2xl font-title line-clamp-2">
                  {article.blogArticleTranslation.title}
                </h3>
              </div>
              <div className="flex min-h-0 items-start self-stretch">
                <div
                  className={cn(
                    'flex-1 min-h-0 text-sm lg:text-base text-inherit flex flex-col items-start [&>p]:mb-4 line-clamp-4',
                    {
                      'md:text-lg': atCarousel,
                    },
                  )}
                  dangerouslySetInnerHTML={{
                    __html: article.blogArticleTranslation.abstract,
                  }}
                />
              </div>
            </Link>
          </div>
          <div
            role="button"
            onClick={() => push(getArticleLink())}
            className={cn(
              'mt-[20px] max-h-[270px] shrink-0 desktop:mt-0 desktop:opacity-0 desktop:max-h-[450px] desktop:transition-all desktop:duration-300 desktop:ease-in-out desktop:group-hover:opacity-100 desktop:group-hover:mt-[20px] flex items-center cursor-pointer',
              {
                'absolute w-full bottom-0 left-0 py-4 pl-4 bg-white desktop:group-hover:mt-2':
                  atCarousel,
              },
            )}>
            <Link
              className={buttonVariants({
                variant: 'default',
                size: 'default',
                className: '!w-fit !p-[20px_40px] desktop:hidden',
              })}
              href={getArticleLink()}>
              {readMoreLabel}
            </Link>
            <AnimatedLink
              className="hidden desktop:block w-fit !mt-0"
              href={getArticleLink()}>
              {readMoreLabel}
            </AnimatedLink>
          </div>
        </div>
      </div>
    </div>
  );
}
